import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Play,
  Trash2
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { formatDate, formatPercentage, formatRelativeTime } from '../utils/formatters';
import toast from 'react-hot-toast';

interface Test {
  id: string;
  status: string;
  totalDocuments: number;
  successfulDocuments: number;
  failedDocuments: number;
  overallAccuracy: number;
  startedAt: string;
  completedAt: string | null;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const TestHistory: React.FC = () => {
  const [tests, setTests] = useState<Test[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/tests?limit=20&offset=0');
      setTests(response.data.tests);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch tests:', error);
      toast.error('Failed to load test history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testId: string) => {
    if (!window.confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/api/tests/${testId}`);
      toast.success('Test deleted successfully');
      fetchTests();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete test');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'running':
        return <Clock className="h-5 w-5 text-yellow-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge-success">Completed</span>;
      case 'running':
        return <span className="badge-warning">Running</span>;
      case 'failed':
        return <span className="badge-error">Failed</span>;
      default:
        return <span className="badge-gray">Unknown</span>;
    }
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 90) return 'text-green-600';
    if (accuracy >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Test History</h1>
            <p className="text-gray-600 mt-2">
              View and manage your test runs and accuracy trends
            </p>
          </div>
          <Link to="/test" className="btn-primary">
            <Play className="h-4 w-4 mr-2" />
            Run New Test
          </Link>
        </div>
      </div>

      {/* Summary Stats */}
      {pagination && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Tests</p>
                <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {tests.filter(t => t.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Running</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {tests.filter(t => t.status === 'running').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Avg Accuracy</p>
                <p className="text-2xl font-bold text-purple-600">
                  {tests.length > 0 ? formatPercentage(
                    tests
                      .filter(t => t.status === 'completed')
                      .reduce((sum, t) => sum + t.overallAccuracy, 0) /
                    tests.filter(t => t.status === 'completed').length || 0
                  ) : '0%'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tests List */}
      <div className="card">
        {tests.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No tests run yet</p>
            <Link to="/test" className="btn-primary">
              <Play className="h-4 w-4 mr-2" />
              Run Your First Test
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {tests.map((test) => (
              <div key={test.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex items-center space-x-4">
                  {getStatusIcon(test.status)}
                  
                  <div>
                    <div className="flex items-center space-x-3">
                      <h3 className="font-medium text-gray-900">
                        Test #{test.id.slice(-8)}
                      </h3>
                      {getStatusBadge(test.status)}
                    </div>
                    
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatRelativeTime(test.startedAt)}</span>
                      </div>
                      
                      <span>•</span>
                      
                      <span>{test.totalDocuments} documents</span>
                      
                      {test.status === 'completed' && (
                        <>
                          <span>•</span>
                          <span>{test.successfulDocuments} successful</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {test.status === 'completed' && (
                    <div className="text-right">
                      <div className={`text-lg font-bold ${getAccuracyColor(test.overallAccuracy)}`}>
                        {formatPercentage(test.overallAccuracy)}
                      </div>
                      <div className="text-xs text-gray-500">Accuracy</div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    {test.status === 'completed' && (
                      <Link
                        to={`/tests/${test.id}`}
                        className="btn-secondary text-sm"
                      >
                        View Report
                      </Link>
                    )}
                    
                    <button
                      onClick={() => handleDelete(test.id)}
                      className="btn-error text-sm"
                      disabled={test.status === 'running'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.hasMore && (
        <div className="mt-6 text-center">
          <button className="btn-secondary">
            Load More Tests
          </button>
        </div>
      )}
    </div>
  );
};